/*
 * Entities named in a tool result.
 *
 * Tool outputs carry a ready-made `ref` (`[label](path "Kind · detail")`) next
 * to each entity they return — on the top-level record and on every item of
 * any list in it. Collecting those lets a row's fold list them as clickable
 * lines instead of a JSON dump. Pure functions; no React.
 */
import { parseEntityHref, type EntityRefTarget } from "./entityRefs";

export const REF_KEY = "ref";

export type ToolResultEntity = {
  label: string;
  /** The link title (`Kind · status · …`), verbatim. */
  title: string | null;
  target: EntityRefTarget;
};

const LINK = /^\[((?:[^[\]\\]|\\.)*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)$/;

/** `[label](path "title")` → its parts, or null when the string is not one link. */
export const parseRefLink = (
  value: unknown
): { label: string; path: string; title: string | null } | null => {
  if (typeof value !== "string") return null;
  const m = LINK.exec(value.trim());
  if (!m) return null;
  return {
    label: m[1].replace(/\\([[\]])/g, "$1"),
    path: m[2],
    title: m[3] ?? null
  };
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const entityOf = (value: unknown): ToolResultEntity | null => {
  const link = parseRefLink(value);
  if (!link) return null;
  const target = parseEntityHref(link.path);
  if (!target) return null;
  return { label: link.label, title: link.title, target };
};

/**
 * Every entity a tool output names, in document order and without repeats:
 * the record's own `ref`, then the `ref` of each item of each list it holds
 * (one level of nesting, which is how the tools shape their results).
 */
export const entitiesFromToolOutput = (output: unknown): ToolResultEntity[] => {
  if (!isRecord(output) || typeof output.error === "string") return [];
  const found: ToolResultEntity[] = [];
  const seen = new Set<string>();
  const push = (e: ToolResultEntity | null) => {
    if (!e || seen.has(e.target.path)) return;
    seen.add(e.target.path);
    found.push(e);
  };
  push(entityOf(output[REF_KEY]));
  for (const [key, value] of Object.entries(output)) {
    if (key === REF_KEY) {
      // Already pushed above.
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (isRecord(item)) push(entityOf(item[REF_KEY]));
        else push(entityOf(item));
      }
    } else if (isRecord(value)) {
      push(entityOf(value[REF_KEY]));
    } else if (key.endsWith("_ref")) {
      push(entityOf(value));
    }
  }
  return found;
};
